import React, { useEffect, useState } from 'react';
import DashboardLayout from './DashboardLayout';
import { Card, Row, Col, ListGroup, Badge, Spinner, Alert, Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaBuilding, 
  FaHourglassHalf, 
  FaEnvelope, 
  FaStar,
  FaComments,
  FaCheckCircle,
  FaClock,
  FaUser
} from 'react-icons/fa';
import { API_BASE_URL } from '../services/api';
import './Dashboard.css';
import './DashboardOverview.css';

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
 
  useEffect(() => {
    fetch(`${API_BASE_URL}/dashboard_overview.php`)
      .then(res => res.json())
      .then(data => {
        setStats(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load dashboard data');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="dashboard-overview-container">
        <div className="loading-container">
          <Spinner animation="border" className="loading-spinner" />
          <p className="mt-3 text-muted">Loading dashboard overview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-overview-container">
        <Alert variant="danger" className="error-alert">
          {error}
        </Alert>
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      icon: FaBuilding,
      value: stats.totalBusinesses || 0,
      label: 'Total Businesses',
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      delay: 0.1
    },
    {
      icon: FaHourglassHalf,
      value: stats.pendingApprovals || 0,
      label: 'Pending Approvals',
      color: '#f6ad55',
      gradient: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
      delay: 0.2
    },
    {
      icon: FaEnvelope,
      value: (stats.recentMessages || []).length,
      label: 'Recent Messages',
      color: '#48bb78',
      gradient: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
      delay: 0.3
    },
    {
      icon: FaStar,
      value: (stats.latestReviews || []).length,
      label: 'Latest Reviews',
      color: '#4299e1',
      gradient: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)',
      delay: 0.4
    }
  ];

  return (
    <DashboardLayout>
      <div className="dashboard-overview-container">
        <Container fluid className="p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header Section */}
            <div className="overview-header">
              <h1 className="overview-title">Dashboard Overview</h1>
              <p className="overview-subtitle">Here is a comprehensive summary of your business activity</p>
            </div>

            {/* Stats Cards */}
            <Row className="mb-4 g-4">
              {statCards.map((stat, index) => (
                <Col key={index} md={3} sm={6} xs={12}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: stat.delay, duration: 0.4 }}
                  >
                    <Card className="overview-stat-card">
                      <Card.Body>
                        <div className="stat-icon-wrapper" style={{ background: stat.gradient }}>
                          <stat.icon className="stat-icon" />
                        </div>
                        <h2 className="stat-value">{stat.value}</h2>
                        <p className="stat-label">{stat.label}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>

            {/* Messages and Reviews Section */}
            <Row className="mb-4 g-4">
              <Col md={6} xs={12}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <Card className="overview-list-card">
                    <Card.Header className="overview-card-header">
                      <div className="d-flex align-items-center">
                        <FaComments className="me-2" />
                        <h5 className="mb-0">Recent Messages & Inquiries</h5>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      {stats.recentMessages && stats.recentMessages.length > 0 ? (
                        <ListGroup variant="flush">
                          {stats.recentMessages.map((msg, index) => (
                            <ListGroup.Item key={msg.id || index} className="overview-list-item">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="message-avatar">
                                      <FaUser />
                                    </div>
                                    <div className="ms-2">
                                      <strong className="message-from">{msg.from || 'Unknown'}</strong>
                                      <Badge bg="secondary" className="ms-2 message-badge">
                                        <FaEnvelope className="me-1" size={10} />
                                        New
                                      </Badge>
                                    </div>
                                  </div>
                                  <p className="message-text mb-0">{msg.message || 'No message content'}</p>
                                </div>
                                <span className="message-time">{msg.time || 'Just now'}</span>
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="empty-state-message">
                          <FaEnvelope size={48} className="text-muted mb-3" />
                          <p className="text-muted">No recent messages</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>

              <Col md={6} xs={12}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  <Card className="overview-list-card">
                    <Card.Header className="overview-card-header">
                      <div className="d-flex align-items-center">
                        <FaStar className="me-2" />
                        <h5 className="mb-0">Latest Reviews</h5>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      {stats.latestReviews && stats.latestReviews.length > 0 ? (
                        <ListGroup variant="flush">
                          {stats.latestReviews.map((review, index) => (
                            <ListGroup.Item key={review.id || index} className="overview-list-item">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="review-rating-badge">
                                      <FaStar className="me-1" />
                                      <span>{review.rating || 'N/A'}</span>
                                    </div>
                                    <span className="review-time ms-2">
                                      <FaClock className="me-1" size={12} />
                                      {review.time || 'Just now'}
                                    </span>
                                  </div>
                                  <p className="review-text mb-0">{review.text || 'No review text'}</p>
                                </div>
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="empty-state-message">
                          <FaStar size={48} className="text-muted mb-3" />
                          <p className="text-muted">No recent reviews</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            </Row>

            {/* Quick Actions Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <Card className="overview-actions-card">
                <Card.Body>
                  <h5 className="mb-3">Quick Actions</h5>
                  <Row className="g-3">
                    <Col md={3} sm={6} xs={12}>
                      <div className="quick-action-item">
                        <FaBuilding className="quick-action-icon" />
                        <span>Manage Businesses</span>
                      </div>
                    </Col>
                    <Col md={3} sm={6} xs={12}>
                      <div className="quick-action-item">
                        <FaEnvelope className="quick-action-icon" />
                        <span>View Messages</span>
                      </div>
                    </Col>
                    <Col md={3} sm={6} xs={12}>
                      <div className="quick-action-item">
                        <FaStar className="quick-action-icon" />
                        <span>Check Reviews</span>
                      </div>
                    </Col>
                    <Col md={3} sm={6} xs={12}>
                      <div className="quick-action-item">
                        <FaCheckCircle className="quick-action-icon" />
                        <span>Approvals</span>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </motion.div>
          </motion.div>
        </Container>
      </div>
    </DashboardLayout>
  );
};

export default DashboardOverview;